import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from './auth';
import { EmptyPane, ErrorBanner, LoadingPane, PageHeader } from './components/page-elements';

const formatPhone = (input = '') => {
  const digits = String(input).replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{3}-\d{3}-\d{4}$/;

const validateShipperForm = (form) => {
  const errors = {};

  if (!form.shipperName?.trim()) {
    errors.shipperName = 'Shipper name is required.';
  }

  if (!form.shipperContactName?.trim()) {
    errors.shipperContactName = 'Contact name is required.';
  }

  if (form.shipperEmail && !EMAIL_REGEX.test(form.shipperEmail)) {
    errors.shipperEmail = 'Enter a valid email address.';
  }

  if (form.shipperPhone && !PHONE_REGEX.test(form.shipperPhone)) {
    errors.shipperPhone = 'Phone must be 10 digits (123-456-7890).';
  }

  return errors;
};

const getCustomerId = (row) => String(row?.customerName?._id || row?.customerName || '');

const Shippers = ({ forcedCustomerId = '', embedded = false }) => {
  const [shippers, setShippers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedShipper, setSelectedShipper] = useState(null);
  const [createFieldErrors, setCreateFieldErrors] = useState({});
  const [editFieldErrors, setEditFieldErrors] = useState({});

  const [createForm, setCreateForm] = useState({
    shipperName: '',
    shipperContactName: '',
    shipperEmail: '',
    shipperPhone: '',
  });

  const [editForm, setEditForm] = useState({
    shipperName: '',
    shipperContactName: '',
    shipperEmail: '',
    shipperPhone: '',
  });

  const user = useMemo(() => getStoredUser(), []);
  const canManage = user?.role === 'admin' || user?.role === 'internal' || user?.role === 'customer';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchShippers = useCallback(async () => {
    try {
      const query = forcedCustomerId ? `?customerId=${forcedCustomerId}` : '';
      const response = await fetch(`${API_BASE_URL}/api/shippers${query}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      const rows = Array.isArray(data) ? data : [];
      const scopedRows = forcedCustomerId
        ? rows.filter((row) => getCustomerId(row) === String(forcedCustomerId))
        : rows;
      setShippers(scopedRows);
      setLoading(false);
    } catch (err) {
      setLoadError(err.message);
      setLoading(false);
    }
  }, [forcedCustomerId]);

  useEffect(() => {
    fetchShippers();
  }, [fetchShippers]);

  const openCreate = () => setCreateOpen(true);

  const closeCreate = () => {
    setCreateOpen(false);
    setActionError('');
    setCreateFieldErrors({});
    setCreateForm({
      shipperName: '',
      shipperContactName: '',
      shipperEmail: '',
      shipperPhone: '',
    });
  };

  const openEdit = (shipper) => {
    setSelectedShipper(shipper);
    setEditForm({
      shipperName: shipper.shipperName || '',
      shipperContactName: shipper.shipperContactName || '',
      shipperEmail: shipper.shipperEmail || '',
      shipperPhone: shipper.shipperPhone || '',
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setSelectedShipper(null);
    setActionError('');
    setEditFieldErrors({});
  };

  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'shipperPhone' ? formatPhone(value) : value;
    setCreateForm((prev) => ({ ...prev, [name]: nextValue }));
    setCreateFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'shipperPhone' ? formatPhone(value) : value;
    setEditForm((prev) => ({ ...prev, [name]: nextValue }));
    setEditFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const normalizedCreatePayload = () => ({
    ...createForm,
    shipperPhone: formatPhone(createForm.shipperPhone),
  });

  const normalizedEditPayload = () => ({
    ...editForm,
    shipperPhone: formatPhone(editForm.shipperPhone),
  });

  const saveCreate = async () => {
    const errors = validateShipperForm(createForm);
    setCreateFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setSaving(true);
    setActionError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/shippers`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          ...normalizedCreatePayload(),
          ...(forcedCustomerId ? { customerName: forcedCustomerId } : {}),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const firstValidationError = err?.errors?.[0]?.msg;
        throw new Error(firstValidationError || err?.message || 'Failed to create shipper');
      }

      const data = await response.json();
      setShippers((prev) => [data.shipper, ...prev]);
      closeCreate();
    } catch (err) {
      setActionError(err.message);
    }

    setSaving(false);
  };

  const saveEdit = async () => {
    if (!selectedShipper?._id) return;

    const errors = validateShipperForm(editForm);
    setEditFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setSaving(true);
    setActionError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/shippers/${selectedShipper._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify(normalizedEditPayload()),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const firstValidationError = err?.errors?.[0]?.msg;
        throw new Error(firstValidationError || err?.message || 'Failed to update shipper');
      }

      const data = await response.json();
      setShippers((prev) => prev.map((s) => (s._id === data.shipper._id ? data.shipper : s)));
      closeEdit();
    } catch (err) {
      setActionError(err.message);
    }

    setSaving(false);
  };

  const deactivateShipper = async (shipperId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/shippers/${shipperId}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ isActive: false }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const firstValidationError = err?.errors?.[0]?.msg;
        throw new Error(firstValidationError || err?.message || 'Failed to deactivate shipper');
      }

      const data = await response.json();
      setShippers((prev) => prev.map((s) => (s._id === data.shipper._id ? data.shipper : s)));
    } catch (err) {
      setActionError(err.message);
    }
  };

  if (loading) return <LoadingPane label="Loading shippers..." />;

  return (
    <Box p={embedded ? 0 : 2}>
      {!embedded ? (
        <PageHeader
          title="Shippers"
          subtitle="Manage shipper contacts and communication details."
          actions={canManage ? [<Button key="add" variant="contained" onClick={openCreate}>Add Shipper</Button>] : null}
        />
      ) : (
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Shippers</Typography>
          {canManage ? <Button variant="contained" onClick={openCreate}>Add Shipper</Button> : null}
        </Box>
      )}
      <ErrorBanner message={loadError || actionError} />

      {shippers.length === 0 ? (
        <EmptyPane title="No shippers found" />
      ) : isMobile ? (
        <Stack spacing={1.2}>
          {shippers.map((shipper) => (
            <Card key={shipper._id} variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.6}>
                  <Typography fontWeight={700}>{shipper.shipperName}</Typography>
                  <Chip size="small" label={shipper.isActive === false ? 'Inactive' : 'Active'} color={shipper.isActive === false ? 'default' : 'success'} />
                </Stack>
                <Typography variant="body2" color="text.secondary">{shipper.shipperContactName || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary">{shipper.shipperEmail || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{shipper.shipperPhone || 'N/A'}</Typography>
                {canManage && (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit shipper"><IconButton color="primary" onClick={() => openEdit(shipper)}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Deactivate shipper"><span><IconButton color="warning" onClick={() => deactivateShipper(shipper._id)} disabled={shipper.isActive === false}><BlockIcon /></IconButton></span></Tooltip>
                  </Stack>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
      <TableContainer component={Paper} elevation={0} className="surface-hover" sx={{ borderRadius: 2, border: '1px solid var(--ui-border)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#ffffff' }}>
              <TableCell>Shipper</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Status</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {shippers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 4 : 3}>No shippers found.</TableCell>
              </TableRow>
            ) : (
              shippers.map((shipper) => (
                <TableRow key={shipper._id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{shipper.shipperName}</Typography>
                  </TableCell>
                  <TableCell>
                    {shipper.shipperContactName || 'N/A'}
                    <br />
                    <Typography variant="body2" color="text.secondary">{shipper.shipperEmail || 'N/A'}</Typography>
                    <Typography variant="body2" color="text.secondary">{shipper.shipperPhone || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={shipper.isActive === false ? 'Inactive' : 'Active'}
                      color={shipper.isActive === false ? 'default' : 'success'}
                    />
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Tooltip title="Edit shipper">
                        <IconButton color="primary" onClick={() => openEdit(shipper)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deactivate shipper">
                        <span>
                          <IconButton
                            color="warning"
                            onClick={() => deactivateShipper(shipper._id)}
                            disabled={shipper.isActive === false}
                          >
                            <BlockIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="sm">
        <DialogTitle>Add Shipper</DialogTitle>
        <DialogContent>
          <TextField margin="normal" name="shipperName" label="Shipper Name" value={createForm.shipperName} onChange={handleCreateChange} fullWidth error={Boolean(createFieldErrors.shipperName)} helperText={createFieldErrors.shipperName} />
          <TextField margin="normal" name="shipperContactName" label="Contact Name" value={createForm.shipperContactName} onChange={handleCreateChange} fullWidth error={Boolean(createFieldErrors.shipperContactName)} helperText={createFieldErrors.shipperContactName} />
          <TextField margin="normal" name="shipperEmail" label="Email" value={createForm.shipperEmail} onChange={handleCreateChange} fullWidth error={Boolean(createFieldErrors.shipperEmail)} helperText={createFieldErrors.shipperEmail} />
          <TextField margin="normal" name="shipperPhone" label="Phone" value={createForm.shipperPhone} onChange={handleCreateChange} fullWidth error={Boolean(createFieldErrors.shipperPhone)} helperText={createFieldErrors.shipperPhone || 'Example: 123-456-7890'} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate}>Cancel</Button>
          <Button variant="contained" onClick={saveCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Shipper'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Shipper</DialogTitle>
        <DialogContent>
          <TextField margin="normal" name="shipperName" label="Shipper Name" value={editForm.shipperName} onChange={handleEditChange} fullWidth error={Boolean(editFieldErrors.shipperName)} helperText={editFieldErrors.shipperName} />
          <TextField margin="normal" name="shipperContactName" label="Contact Name" value={editForm.shipperContactName} onChange={handleEditChange} fullWidth error={Boolean(editFieldErrors.shipperContactName)} helperText={editFieldErrors.shipperContactName} />
          <TextField margin="normal" name="shipperEmail" label="Email" value={editForm.shipperEmail} onChange={handleEditChange} fullWidth error={Boolean(editFieldErrors.shipperEmail)} helperText={editFieldErrors.shipperEmail} />
          <TextField margin="normal" name="shipperPhone" label="Phone" value={editForm.shipperPhone} onChange={handleEditChange} fullWidth error={Boolean(editFieldErrors.shipperPhone)} helperText={editFieldErrors.shipperPhone || 'Example: 123-456-7890'} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Shippers;

